import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import AuthLayout from '../../components/layout/AuthLayout';
import Spinner from '../../components/common/Spinner';
import authService from '../../api/services/auth.service';
import useAuthStore, { isProfileComplete } from '../../store/useAuthStore';
import { showToast } from '../../store/useToastStore';
import { REMEMBER_DEVICE_DAYS, findToken, forgetDevice, rememberDevice } from '../../utils/token';
import { fmtCountdown } from '../../utils/formatters';
import { RECAPTCHA_SITE_KEY } from '../../utils/constants';
import { PATHS } from '../../routes/navConfig';

const extractUser = (res) => res?.user || res?.data?.user || null;
const extractRefreshToken = (res) => res?.refresh_token || res?.data?.refresh_token || null;

/* Resend stays locked this long after any send — the first one that brought the
   person here, and each resend after it. */
const RESEND_COOLDOWN_MS = 2 * 60 * 1000;

export default function VerifyCodePage() {
  const { phone, username, login, otpSentAt, markOtpSent } = useAuthStore();
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  /* Ticked by default, per the client (HANDOFF, 5 Oct: "Keep me signed in"). */
  const [rememberThisDevice, setRememberThisDevice] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  /* Resend needs a fresh reCAPTCHA token like the first send did, so the box
     appears on demand: click Resend → tick the box → the API call goes out. */
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const recaptchaRef = useRef(null);
  const boxRefs = useRef([]);
  const navigate = useNavigate();

  /* Cooldown clock: `now` only ticks while a lock is running, so the interval
     isn't left going for the rest of the visit. The min() clamp covers the
     moment right after a resend, when `now` is still the stale value from
     before the clock stopped. */
  const [now, setNow] = useState(Date.now());
  const cooldownLeft = Math.min(RESEND_COOLDOWN_MS, Math.max(0, otpSentAt + RESEND_COOLDOWN_MS - now));
  const coolingDown = cooldownLeft > 0;
  useEffect(() => {
    if (!coolingDown) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [coolingDown]);

  /* react-google-recaptcha never resets its widget on unmount, so a hidden or
     abandoned box would leave an orphaned grecaptcha client whose timers later
     throw. Reset it whenever the box goes away (hide or page leave). The ref is
     read after mount, inside the effect — it's null until the box is shown. */
  useEffect(() => {
    if (!captchaOpen) return undefined;
    const widget = recaptchaRef.current;
    return () => {
      try {
        widget?.reset();
      } catch {
        /* widget already torn down */
      }
    };
  }, [captchaOpen]);

  const complete = digits.every((d) => d.length === 1);

  const setDigit = (i, raw) => {
    const v = raw.replace(/[^0-9]/g, '').slice(0, 1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && boxRefs.current[i + 1]) boxRefs.current[i + 1].focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && boxRefs.current[i - 1]) boxRefs.current[i - 1].focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').replace(/[^0-9]/g, '').slice(0, 6).split('');
    const next = ['', '', '', '', '', ''];
    pasted.forEach((d, idx) => { next[idx] = d; });
    setDigits(next);
    const last = boxRefs.current[Math.min(pasted.length, 6) - 1];
    if (last) last.focus();
  };

  const verifyCode = async () => {
    if (!complete || verifying) return;
    setVerifying(true);
    try {
      const res = await authService.verifyOtp(username, digits.join(''));
      const user = extractUser(res);
      const token = findToken(res);
      if (!token) {
        // eslint-disable-next-line no-console
        console.warn('verify response carried no token — session persistence relies on the stored user/cookies.');
      }
      /* Ticked: keep the refresh token so the session renews itself for 30 days.
         Unticked: only the access token, which ends the session after its 24 hours. */
      const refreshToken = extractRefreshToken(res);
      if (rememberThisDevice && refreshToken) rememberDevice(refreshToken);
      else forgetDevice();
      login(token, user);
      /* A number that's new here (or that never finished onboarding in the app)
         has no profile, class or school yet — the portal has nothing to show
         until sign-up fills those in. */
      navigate(isProfileComplete(user) ? PATHS.DASHBOARD : PATHS.SIGNUP);
    } catch (err) {
      showToast(err.message);
      setDigits(['', '', '', '', '', '']);
      boxRefs.current[0]?.focus();
      setVerifying(false);
    }
  };

  /* step 1: the link only reveals the box */
  const startResend = () => {
    if (resending || coolingDown) return;
    setCaptchaOpen(true);
  };

  /* step 2: ticking the box hands over the token and fires the send. The lock
     starts when a code actually goes out — not on the click — so an abandoned
     captcha doesn't cost the person two minutes for nothing. */
  const resendCode = async (captchaToken) => {
    if (!captchaToken || resending) return;
    setResending(true);
    try {
      await authService.sendOtp(username, captchaToken);
      markOtpSent();
      showToast('Code resent ✓');
      setCaptchaOpen(false);
    } catch (err) {
      showToast(err.message);
      /* tokens are single-use — the box has to be ticked again for a retry */
      try {
        recaptchaRef.current?.reset();
      } catch {
        /* stale grecaptcha widget */
      }
    } finally {
      setResending(false);
    }
  };

  /* Direct visit / refresh without initiating login first — nothing to verify. */
  if (!username) return <Navigate to={PATHS.LOGIN} replace />;

  return (
    <AuthLayout>
      <h1>Enter your code</h1>
      <p className="sub">
        We sent a 6-digit code to <b style={{ color: 'var(--ink)' }}>{phone}</b>.{' '}
        <button className="switch-link" onClick={() => navigate(PATHS.LOGIN)}>Change number</button>
      </p>
      <div className="form-stack">
        <div className="code-row">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (boxRefs.current[i] = el)}
              className="code-box"
              maxLength={1}
              inputMode="numeric"
              value={d}
              autoFocus={i === 0}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
            />
          ))}
        </div>
        <label className="keep-row">
          <input
            type="checkbox"
            checked={rememberThisDevice}
            onChange={(e) => setRememberThisDevice(e.target.checked)}
          />
          Remember this device for {REMEMBER_DEVICE_DAYS} days
        </label>
        <button className="btn btn-primary btn-primary-full" disabled={!complete || verifying} onClick={verifyCode}>
          {verifying ? <><Spinner /> Verifying…</> : 'Verify & Continue'}
        </button>
        <p style={{ fontSize: '12.5px', color: 'var(--muted)', textAlign: 'center', margin: 0 }}>
          Didn't get a code?{' '}
          <button className="switch-link" onClick={startResend} disabled={resending || coolingDown || captchaOpen}>
            {resending ? 'Resending…' : coolingDown ? `Resend in ${fmtCountdown(cooldownLeft)}` : 'Resend'}
          </button>
        </p>
        {captchaOpen && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={(token) => resendCode(token)}
            />
            <span className="hint">Tick the box and we'll send you a fresh code.</span>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
